import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './QuickBookFAB.css';
import { createBookingHandoff } from '../../../../redux/slices/userSessionSlice';

const normPhone = (p) => {
  let s = String(p || '').replace(/[^\d]/g, '');
  if (!s) return '';
  if (s.startsWith('972')) return '+972' + s.slice(3);
  if (s.startsWith('0')) return '+972' + s.slice(1);
  return '+' + s;
};

const getInitials = (firstName = '', lastName = '') =>
  `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';

const AVATAR_COLORS = ['#26264F', '#10B981', '#E99781', '#3B82F6', '#8B5CF6'];

const QuickBookFABInner = ({ loadedMasters = {}, showOnDesktop = false }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || 'ru';
  const isHebrew = lang === 'he';
  const dispatch = useDispatch();

  const client = useSelector(s => s.userSession?.currentAccount || s.users?.currentUser);

  // переход на co.il с одноразовым токеном авторизации
  
  const goToBooking = async (masterLink) => {
  if (!masterLink) return;
  const path = String(masterLink).replace(/^\/+/, '');
  let url = `https://zmanup.co.il/${lang}/${path}?from=cabinet`;
  try {
    const handoffToken = await dispatch(createBookingHandoff()).unwrap();
    if (handoffToken) url += `&ct=${handoffToken}`;
  } catch { /* не залогинен / ошибка — уходим гостем */ }
  window.location.assign(url);
};

  const allAppointments = useSelector(s => s.appointments?.allAppointments || []);

  const [modalOpen, setModalOpen] = useState(false);

  const frequentMasters = useMemo(() => {
    if (!client?.phone) return [];
    const myPhone = normPhone(client.phone);
    const countMap = {};
    allAppointments
      .filter(a => normPhone(a?.phoneContact) === myPhone)
      .forEach(a => {
        if (a.uid) countMap[a.uid] = (countMap[a.uid] || 0) + 1;
      });
    return Object.entries(countMap)
      .sort((a, b) => b[1] - a[1])
      .map(([uid, count]) => ({ uid, count }));
  }, [allAppointments, client?.phone]);

  const visibleMasters = useMemo(() =>
    frequentMasters.filter(({ uid }) => loadedMasters[uid]?.masterLink),
    [frequentMasters, loadedMasters]
  );

  if (visibleMasters.length === 0) return null;

  const handleFabClick = () => {
    if (modalOpen) {
      setModalOpen(false);
      return;
    }
    if (visibleMasters.length === 1) {
      const master = loadedMasters[visibleMasters[0].uid];
      goToBooking(master.masterLink);
    } else {
      setModalOpen(true);
    }
  };

  const handleSelectMaster = (master) => {
    setModalOpen(false);
    goToBooking(master.masterLink);
  };

  const visitsLabel = (count) => {
    const n = Number(count);
    if (lang === 'he') return `${n} ביקורים`;
    if (lang === 'en') return `${n} visit${n !== 1 ? 's' : ''}`;
    if (n === 1) return `${n} визит`;
    if (n >= 2 && n <= 4) return `${n} визита`;
    return `${n} визитов`;
  };

  const bookLabel = lang === 'he' ? 'לקבוע תור' : lang === 'en' ? 'Book again' : 'Записаться снова';
  const modalTitle = lang === 'he' ? 'לאיזה מאסטר?' : lang === 'en' ? 'Choose a master' : 'К кому записаться?';
  const modalSub = lang === 'he' ? 'מאסטרים שביקרת' : lang === 'en' ? 'Masters you visited' : 'Мастера, которых вы посещали';

  return (
    <>
      {/* FAB — при открытой модалке показываем X, иначе иконку календаря */}
      <button
        type="button"
        className={`qb-fab ${isHebrew ? 'rtl' : ''} ${showOnDesktop ? 'desktop-visible' : ''} ${modalOpen ? 'qb-fab--open' : ''}`}
        onClick={handleFabClick}
        aria-label={modalOpen ? 'Закрыть' : bookLabel}
      >
        {modalOpen ? (
          // Крестик
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          // Календарь
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
            stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
            <line x1="12" y1="14" x2="12" y2="18" />
            <line x1="10" y1="16" x2="14" y2="16" />
          </svg>
        )}
      </button>

      {/* Лейбл — только когда модалка закрыта */}
      {!modalOpen && (
        <div className={`qb-fab-label ${isHebrew ? 'rtl' : ''}`}>
          {bookLabel}
        </div>
      )}

      {/* Модалка */}
      {modalOpen && (
        <div className="qb-modal-bg" onClick={() => setModalOpen(false)}>
          <div
            className={`qb-modal ${isHebrew ? 'rtl' : ''}`}
            onClick={e => e.stopPropagation()}
            dir={isHebrew ? 'rtl' : 'ltr'}
          >
            {/* Хэндл */}
            <div className="qb-modal__handle" />

            {/* Шапка с крестиком */}
            <div className="qb-modal__header">
              <div>
                <p className="qb-modal__title">{modalTitle}</p>
                <p className="qb-modal__sub">{modalSub}</p>
              </div>
              <button
                className="qb-modal__close"
                onClick={() => setModalOpen(false)}
                aria-label="Закрыть"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Список мастеров */}
            <div className="qb-modal__list">
              {visibleMasters.map(({ uid, count }, idx) => {
                const master = loadedMasters[uid];
                if (!master) return null;
                return (
                  <button
                    key={uid}
                    className="qb-master-row"
                    onClick={() => handleSelectMaster(master)}
                  >
                    <div className="qb-master-row__ava"
                      style={{ background: AVATAR_COLORS[idx % AVATAR_COLORS.length] }}>
                      {getInitials(master.firstName, master.lastName)}
                    </div>
                    <div className="qb-master-row__info">
                      <div className="qb-master-row__name">
                        {master.firstName} {master.lastName}
                      </div>
                      {master.profession && (
                        <div className="qb-master-row__meta">{master.profession}</div>
                      )}
                    </div>
                    <span className="qb-master-row__badge">{visitsLabel(count)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const QuickBookFAB = (props) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || typeof document === 'undefined') return null;

  return createPortal(<QuickBookFABInner {...props} />, document.body);
};

export default QuickBookFAB;